import { create } from 'zustand';
import { adminRequisitionApi } from '@api/api-config.ts';
import { AdminRequisitionDetailResponse, AdminRequisitionListResponse, RequisitionStatus } from '@api/generated/portal-be-admin';

export interface RequisitionFilters {
  q?: string;
  status?: RequisitionStatus;
  page: number;
  size: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

interface AdminRequisitionStoreState {
  loading: boolean;
  error: string | null;
  data: AdminRequisitionListResponse[] | null;
  pagination: PaginationInfo | null;
  detail: AdminRequisitionDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;
  loadRequisitions: (filters: RequisitionFilters) => Promise<void>;
  loadDetail: (requisitionId: number) => Promise<void>;
  clearDetail: () => void;
}

const extractApiError = (error: unknown): string | null => {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || (error instanceof Error ? error.message : null);
};

export const useAdminRequisitionStore = create<AdminRequisitionStoreState>(set => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  detail: null,
  detailLoading: false,
  detailError: null,

  loadRequisitions: async (filters: RequisitionFilters) => {
    set({ loading: true, error: null });
    try {
      const response = await adminRequisitionApi.getAdminRequisitions({
        page: filters.page,
        size: filters.size,
        q: filters.q,
        status: filters.status,
      });
      const result = response.data;
      set({
        loading: false,
        data: result.items || [],
        pagination: { page: result.page, size: result.size, total: result.total, totalPages: result.totalPages },
      });
    } catch (error) {
      set({ loading: false, error: extractApiError(error) ?? 'Chyba při načítání žádanek' });
    }
  },

  loadDetail: async (requisitionId: number) => {
    set({ detailLoading: true, detail: null, detailError: null });
    try {
      const response = await adminRequisitionApi.getAdminRequisitionDetail({ requisitionId });
      set({ detailLoading: false, detail: response.data });
    } catch (error) {
      set({ detailLoading: false, detailError: extractApiError(error) ?? 'Chyba při načítání žádanky' });
    }
  },

  clearDetail: () => set({ detail: null, detailError: null }),
}));
